import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getLink } from "../../dataProvider/links.js";
import { changePassword, isValidEmail } from "../../dataProvider/user";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLock, faEnvelope, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import TagManager from "react-gtm-module";
import { updateUser, reloadUser } from "../../actions/user";
import { error as logError } from "../../logger";
import { Modal } from "react-bootstrap";

import downloadButton from "../../img/v2/home/upload/download-button.png";
import confirmationLogoV2 from "../../img/v2/payment/confirmationLogo.png";

import "../../scss/v2/payment.scss"

function Confirm(props) {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(true);
  const [modalEmail, setModalEmail] = useState(props.user?.email || "");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd1, setShowPwd1] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [modalError, setModalError] = useState("");
  const [modalWarning, setModalWarning] = useState("");
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (!props.products.length) return;

    const purchaseKey = `purchase_tracked_${props.user.id}`;
    if (localStorage.getItem(purchaseKey)) return;

    const customerProduct = props.products.find((product) => product.id === props.user.products[0]);
    if (!customerProduct) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "purchase",
        ecommerce: {
          currencyCode: customerProduct.currency.toUpperCase(),
          purchase: {
            actionField: {
              id: props.user.id,
              revenue: customerProduct.amount / 100,
            },
            products: [
              {
                name: customerProduct.name,
                id: customerProduct.id,
                price: customerProduct.acquisitionAmount / 100,
                quantity: 1,
              },
            ],
          },
        },
      },
    });

    localStorage.setItem(purchaseKey, "true");
  }, [props.user, props.products]);

  const handleModalSubmit = useCallback(async () => {
    try {
      setModalError("");

      const emailChanged =
        modalEmail && modalEmail.trim() !== (props.user?.email || "").trim();
      if (emailChanged && !isValidEmail(modalEmail)) {
        setModalError(t("paymentConfirmation.modal.error.invalidEmail"));
        return;
      }

      if (pwd || pwd2) {
        if (pwd.length < 6) {
          setModalError(t("paymentConfirmation.modal.error.tooShort"));
          return;
        }
        if (pwd !== pwd2) {
          setModalError(t("paymentConfirmation.modal.error.notIdentical"));
          return;
        }
      }

      setPending(true);

      if (emailChanged) {
        await props.dispatch(updateUser({ id: props.user.id, email: modalEmail.trim() }));
      }
      if (pwd) {
        await changePassword(props.user.id, pwd, i18n.language);
      }

      await props.dispatch(reloadUser());
      setShowModal(false);
    } catch (e) {
      logError(e);
      setModalError(t("paymentConfirmation.modal.error.generic"));
    } finally {
      setPending(false);
    }
  }, [modalEmail, pwd, pwd2, props, t]);

  return (
    <>
      <Modal
        show={showModal}
        size="lg"
        centered
        keyboard={false}
        onHide={() => setModalWarning(true)}
      >
        <Modal.Header closeButton>
          <div className="w-100 text-center">
            <h2 className="mb-1">
              {t("paymentConfirmation.modal.title")}
            </h2>
            <p className="mb-0">
              {t("paymentConfirmation.modal.subtitle")}
            </p>
          </div>
        </Modal.Header>

        <Modal.Body>
          {modalError && <div className="alert alert-danger">{modalError}</div>}
          {modalWarning && <div className="alert alert-warning"> {t("paymentConfirmation.modal.hide")}</div>}

          {/* Email */}
          <div className="form-group">
            <label className="font-weight-bold">
              {t("paymentConfirmation.email.label")}
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faEnvelope} />
                </span>
              </div>
              <input
                type="email"
                className="form-control"
                placeholder={t("paymentConfirmation.email.placeholder")}
                value={modalEmail}
                autoComplete="email"
                onChange={(e) => setModalEmail(e.target.value)}
              />
            </div>
          </div>

          {/* New password */}
          <div className="form-group">
            <label className="font-weight-bold">
              {t("paymentConfirmation.password.label")}
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faLock} />
                </span>
              </div>
              <input
                type={showPwd1 ? "text" : "password"}
                className="form-control"
                placeholder={t("paymentConfirmation.password.placeholder")}
                minLength={6}
                maxLength={50}
                autoComplete="new-password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
              />
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPwd1((v) => !v)}
                  disabled={pending}
                >
                  <FontAwesomeIcon icon={showPwd1 ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
          </div>

          {/* Retype password */}
          <div className="form-group mb-0">
            <label className="font-weight-bold">
              {t("paymentConfirmation.passwordRetyped.label")}
            </label>
            <div className="input-group">
              <div className="input-group-prepend">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faLock} />
                </span>
              </div>
              <input
                type={showPwd2 ? "text" : "password"}
                className="form-control"
                placeholder={t("paymentConfirmation.passwordRetyped.placeholder")}
                autoComplete="new-password"
                value={pwd2}
                onChange={(e) => setPwd2(e.target.value)}
              />
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPwd2((v) => !v)}
                  disabled={pending}
                >
                  <FontAwesomeIcon icon={showPwd2 ? faEyeSlash : faEye} />
                </button>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            className="button orange-button"
            disabled={pending}
            onClick={handleModalSubmit}
          >
            {pending ? "..." : t("paymentConfirmation.modal.submit")}
          </button>
        </Modal.Footer>
      </Modal>
      <div className="payment-confirmation container">
        <div className="row">
          <div className="col-12 col-lg-8 offset-lg-2">
            <img className="img img-fluid payment-confirmation-image" src={confirmationLogoV2} alt={confirmationLogoV2} />
            <h1 className="payment-confirmation-title">
              {t("paymentConfirmation.title")}
            </h1>
            <div
              className="payment-confirmation-text"
              dangerouslySetInnerHTML={{
                __html: t("paymentConfirmation.text"),
              }}
            />
          </div>
          <div className="col-12 col-lg-6 offset-lg-3">
            <Link to={getLink("cloud", i18n.language)}>
              <button className="button orange-button payment-confirmation-button">
                <img className="img img-fluid" src={downloadButton} alt={downloadButton} />
                {t("paymentConfirmation.button")}
              </button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user.user,
  products: state.product.products,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(Confirm);
